import { useMemo } from 'react';
import { bbbeeScorecardRoute, portal, ProtectedRoute, RouteView } from '../Component/Common/Constants/routes';
import { legacyRouteMap } from './legacyRouteConfig';
import { getNativeReactElementComponents, NativeReactElementComponents } from './nativeReactElementOverrides';
import { useBEELegacyPages } from './useBEELegacyPages';

const viewNameToOverrideKey: Record<string, keyof NativeReactElementComponents> = {
    'Dashboard': 'dashboard',
    'Dashboards': 'dashboard',
    'Scorecard': 'scorecard',
    'Data Page': 'dataPage',
};

const getNativeReactViewProps = (
    overrideKey: keyof NativeReactElementComponents,
    route: ProtectedRoute,
    nativeComponents: NativeReactElementComponents
): any => {
    switch (overrideKey) {
        case 'dataPage':
            return {
                pageTitle: 'Data Page',
                elementName: route.name,
                tabs: nativeComponents.dataPageTabs,
            };
        default:
            return undefined;
    }
};

export const useBEEElementRoutes = () => {
    const { routes, loading, scorecardTypeId } = useBEELegacyPages();

    const resolvedRoutes = useMemo(() => {
        return routes.map((route: ProtectedRoute) => {
            const nativeComponents = getNativeReactElementComponents(route.elementId, scorecardTypeId);
            if (!nativeComponents || !route.views) {
                return route;
            }

            const views: RouteView[] = route.views.map(view => {
                const overrideKey = viewNameToOverrideKey[view.name];
                const nativeComponent = overrideKey && nativeComponents[overrideKey];
                if (!nativeComponent) {
                    return view;
                }

                return {
                    ...view,
                    Component: nativeComponent,
                    componentProps: getNativeReactViewProps(overrideKey, route, nativeComponents),
                };
            });

            if (nativeComponents.additionalViews && nativeComponents.additionalViews.length > 0) {
                const baseLegacyRoute = legacyRouteMap[route.name] || route.name.toLowerCase().replace(/\s+/g, '');
                nativeComponents.additionalViews.forEach(additionalView => {
                    views.push({
                        path: `${bbbeeScorecardRoute}${baseLegacyRoute}/${additionalView.routeSegment}`,
                        name: additionalView.name,
                        mini: additionalView.mini,
                        Component: additionalView.Component,
                        layout: portal,
                    });
                });
            }

            return { ...route, views };
        });
    }, [routes, scorecardTypeId]);

    return { routes: resolvedRoutes, loading };
};
